import React, { useState, useEffect } from "react";
import { MessageCircle } from "lucide-react";
import { useChat } from "./useChat";
import { ChatPanel, IncomingCallBanner } from "./ChatPanel";

// Floating chat bubble, mounted once (in App.js) for every authenticated
// user — same badge treatment as the notification bell in layout/Header.jsx,
// but anchored bottom-right and self-contained so it works from any page.
// The panel's open/closed state is a per-browser convenience, not real
// data — persisted so a page reload doesn't look like the conversation
// "vanished" (the messages themselves are always safe on the server; only
// which thread is showing is local UI state). Guarded against throwing in
// private-browsing/blocked-storage contexts.
const readOpen = () => { try { return localStorage.getItem('hrms_chat_open') === 'true'; } catch { return false; } };
const writeOpen = (v) => { try { localStorage.setItem('hrms_chat_open', String(v)); } catch { /* ignore */ } };

export const ChatWidget = ({ myUserId, isAdmin }) => {
  const [open, setOpen] = useState(readOpen);
  const chat = useChat(!!myUserId, myUserId);
  const { setPanelOpen } = chat;

  useEffect(() => { setPanelOpen(open); }, [open, setPanelOpen]);

  if (!myUserId) return null;

  const toggle = () => setOpen(o => { const next = !o; writeOpen(next); return next; });
  const close = () => { setOpen(false); writeOpen(false); };

  return (
    <div className="fixed bottom-5 right-5 z-[250] flex flex-col items-end">
      <IncomingCallBanner chat={chat} />
      {open && (
        <ChatPanel chat={chat} myUserId={myUserId} isAdmin={isAdmin} onClose={close} />
      )}
      <button
        onClick={toggle}
        className="relative w-[52px] h-[52px] rounded-full bg-gradient-to-br from-p to-[#1E3A8A] text-white flex items-center justify-center border-0 cursor-pointer shadow-[0_8px_24px_rgba(37,99,235,0.4)] hover:shadow-[0_10px_28px_rgba(37,99,235,0.5)] transition-shadow mt-3"
      >
        <MessageCircle size={22} />
        {!open && chat.unreadTotal > 0 && (
          <span className="absolute -top-[2px] -right-[2px] min-w-[19px] h-[19px] px-[4px] rounded-full bg-r text-white text-[12px] font-bold flex items-center justify-center border-2 border-bg1">
            {chat.unreadTotal > 99 ? '99+' : chat.unreadTotal}
          </span>
        )}
      </button>
    </div>
  );
};
